import React from 'react';
import { UserProfile, LIRA_CONVERSION_STRATEGIES, LIRA_PROVINCES, RRSP_RRIF_CONVERSION_STRATEGIES } from '../../types/advanced-retirement';
import FormattedInput from '../FormattedInput';

interface FinancialBalancesSectionProps {
  userProfile: UserProfile;
  handleInputChange: (field: keyof UserProfile, value: any) => void;
}

const FinancialBalancesSection: React.FC<FinancialBalancesSectionProps> = ({
  userProfile,
  handleInputChange
}) => {
  return (
    <div className="form-section" style={{maxWidth: '1200px', margin: '0 auto'}}>
      <h3>Financial Balances</h3>
      <div className="form-grid">
        <div className="form-group">
          <label>{userProfile.spouseAge ? 'RRSP/RRIF Balances ($)' : 'RRSP/RRIF Balance ($)'}</label>
          <div className="side-by-side-inputs">
            <div className="input-group">
              <label style={{ fontWeight: 'normal', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                {userProfile.spouseAge ? 'My RRSP/RRIF Balance' : 'Current RRSP/RRIF Balance'}
              </label>
              <FormattedInput
                defaultText="Default: $250,000"
                value={userProfile.rrspBalance}
                onChange={(value) => handleInputChange('rrspBalance', value)}
                formatOptions={{ type: 'currency', decimalPlaces: 0 }}
              />
            </div>
            {userProfile.spouseAge && (
              <div className="input-group">
                <label style={{ fontWeight: 'normal', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                  Spouse RRSP/RRIF Balance
                </label>
                <FormattedInput
                  defaultText="Default: $200,000"
                  value={userProfile.spouseRrspBalance || 0}
                  onChange={(value) => handleInputChange('spouseRrspBalance', value)}
                  formatOptions={{ type: 'currency', decimalPlaces: 0 }}
                />
              </div>
            )}
          </div>
          
          {/* RRSP Contribution Room */}
          <div className="side-by-side-inputs">
            <div className="input-group">
              <label style={{ fontWeight: 'normal', fontSize: '0.9rem', marginBottom: '0.5rem', textAlign: 'center' }}>
                  Contribution Room
                </label>
              <FormattedInput
                defaultText="Default: $30,000"
                value={userProfile.rrspContributionRoom ?? 30000}
                onChange={(value) => handleInputChange('rrspContributionRoom', value)}
                formatOptions={{ type: 'currency', decimalPlaces: 0 }}
              />
            </div>
            {userProfile.spouseAge && (
              <div className="input-group">
                <label style={{ fontWeight: 'normal', fontSize: '0.9rem', marginBottom: '0.5rem', textAlign: 'center' }}>
                  Contribution Room
                </label>
                <FormattedInput
                  defaultText="Default: $25,000"
                  value={userProfile.spouseRrspContributionRoom ?? 25000}
                  onChange={(value) => handleInputChange('spouseRrspContributionRoom', value)}
                  formatOptions={{ type: 'currency', decimalPlaces: 0 }}
                />
              </div>
            )}
          </div>
        </div>

        {/* RRSP → RRIF Conversion Strategy */}
        <div className="form-group">
          <label>RRSP → RRIF Conversion Strategy</label>
          <div className="side-by-side-inputs">
            <div className="input-group">
              {userProfile.spouseAge && <small>My Strategy</small>}
              <select
                value={userProfile.rrspRrifConversionStrategy || 'Calculate Optimal Conversion Age'}
                onChange={(e) => handleInputChange('rrspRrifConversionStrategy', e.target.value)}
              >
                {RRSP_RRIF_CONVERSION_STRATEGIES.map(strategy => (
                  <option key={strategy} value={strategy}>{strategy}</option>
                ))}
              </select>
            </div>
            {userProfile.spouseAge && (
              <div className="input-group">
                <small>Spouse Strategy</small>
                <select
                  value={userProfile.spouseRrspRrifConversionStrategy || 'Calculate Optimal Conversion Age'}
                  onChange={(e) => handleInputChange('spouseRrspRrifConversionStrategy', e.target.value)}
                >
                  {RRSP_RRIF_CONVERSION_STRATEGIES.map(strategy => (
                    <option key={strategy} value={strategy}>{strategy}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <small style={{ color: '#666', fontSize: '0.85rem', display: 'block', marginTop: '0.5rem' }}>
            <strong>Why convert early?</strong> (1) Pension income splitting at age 65+ (share up to 50% with spouse),
            (2) $2,000 federal pension tax credit at 65+, (3) Lower withdrawal minimums (4% at 65 vs 5.28% at 71).
            Convert during "grey years" for semi-retirement income. Maximum age 71 (CRA rule).
          </small>
        </div>

        <div className="form-group">
          <label>LIRA Balance ($)</label>
          <div className="side-by-side-inputs">
            <div className="input-group">
              {userProfile.spouseAge && <small>My LIRA</small>}
              <FormattedInput
                defaultText="Default: $0"
                value={userProfile.liraBalance || 0}
                onChange={(value) => handleInputChange('liraBalance', value)}
                formatOptions={{ type: 'currency', decimalPlaces: 0 }}
              />
            </div>
            {userProfile.spouseAge && (
              <div className="input-group">
                <small>Spouse LIRA</small>
                <FormattedInput
                  defaultText="Default: $0"
                  value={userProfile.spouseLiraBalance || 0}
                  onChange={(value) => handleInputChange('spouseLiraBalance', value)}
                  formatOptions={{ type: 'currency', decimalPlaces: 0 }}
                />
              </div>
            )}
          </div>
          <small style={{ color: '#666', fontSize: '0.85rem', display: 'block', marginTop: '0.5rem' }}>
            <strong>LIRA</strong> (Locked-In Retirement Account) - Created when your DC pension plan transfers after leaving a job.
            Grows tax-deferred, then converts to a LIF (Life Income Fund). LIF withdrawals follow RRIF minimum rates.
          </small>
        </div>

        {/* LIRA Province - Only show if LIRA balance > 0 */}
        {(userProfile.liraBalance > 0 || (userProfile.spouseAge && (userProfile.spouseLiraBalance || 0) > 0)) && (
          <div className="form-group">
            <label>LIRA Province <span style={{ color: 'red' }}>*</span></label>
            <div className="side-by-side-inputs">
              {userProfile.liraBalance > 0 && (
                <div className="input-group">
                  {userProfile.spouseAge && <small>My LIRA Province</small>}
                  <select
                    value={userProfile.liraProvince || ''}
                    onChange={(e) => handleInputChange('liraProvince', e.target.value)}
                    style={{
                      borderColor: userProfile.liraBalance > 0 && !userProfile.liraProvince ? '#ff6b6b' : undefined,
                      backgroundColor: userProfile.liraBalance > 0 && !userProfile.liraProvince ? '#fff5f5' : undefined
                    }}
                  >
                    <option value="">-- Select Province --</option>
                    <optgroup label="100% Unlocking (Full Flexibility)">
                      {LIRA_PROVINCES.filter(p => p.unlocking === '100%').map(province => (
                        <option key={province.value} value={province.value}>
                          {province.label}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="50% Unlocking (Partial Flexibility)">
                      {LIRA_PROVINCES.filter(p => p.unlocking === '50%').map(province => (
                        <option key={province.value} value={province.value}>
                          {province.label}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="0% Unlocking (No Flexibility)">
                      {LIRA_PROVINCES.filter(p => p.unlocking === '0%').map(province => (
                        <option key={province.value} value={province.value}>
                          {province.label}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                </div>
              )}
              {userProfile.spouseAge && userProfile.spouseLiraBalance && userProfile.spouseLiraBalance > 0 && (
                <div className="input-group">
                  <small>Spouse LIRA Province</small>
                  <select
                    value={userProfile.spouseLiraProvince || ''}
                    onChange={(e) => handleInputChange('spouseLiraProvince', e.target.value)}
                    style={{
                      borderColor: !userProfile.spouseLiraProvince ? '#ff6b6b' : undefined,
                      backgroundColor: !userProfile.spouseLiraProvince ? '#fff5f5' : undefined
                    }}
                  >
                    <option value="">-- Select Province --</option>
                    <optgroup label="100% Unlocking">
                      {LIRA_PROVINCES.filter(p => p.unlocking === '100%').map(province => (
                        <option key={province.value} value={province.value}>
                          {province.label}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="50% Unlocking">
                      {LIRA_PROVINCES.filter(p => p.unlocking === '50%').map(province => (
                        <option key={province.value} value={province.value}>
                          {province.label}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="0% Unlocking">
                      {LIRA_PROVINCES.filter(p => p.unlocking === '0%').map(province => (
                        <option key={province.value} value={province.value}>
                          {province.label}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                </div>
              )}
            </div>
            <small style={{ color: '#666', fontSize: '0.85rem', display: 'block', marginTop: '0.5rem' }}>
              <strong>Province determines unlocking rules:</strong> When converting LIRA → LIF, some provinces allow you to unlock
              a portion to RRSP (100% in MB/SK, 50% in AB/ON/PE/Federal, 0% in BC/QC/NB/NS/NL). Unlocked funds move to RRSP with more flexibility.
            </small>
          </div>
        )}

        {/* LIRA Conversion Strategy - Optimization Field */}
        {(userProfile.liraBalance > 0 || (userProfile.spouseAge && (userProfile.spouseLiraBalance || 0) > 0)) && (
          <div className="form-group">
            <label>LIRA → LIF Conversion Strategy</label>
            <div className="side-by-side-inputs">
              {userProfile.liraBalance > 0 && (
                <div className="input-group">
                  {userProfile.spouseAge && <small>My Strategy</small>}
                  <select
                    value={userProfile.liraConversionStrategy || 'Calculate Optimal Conversion Age'}
                    onChange={(e) => handleInputChange('liraConversionStrategy', e.target.value)}
                  >
                    {LIRA_CONVERSION_STRATEGIES.map(strategy => (
                      <option key={strategy} value={strategy}>{strategy}</option>
                    ))}
                  </select>
                </div>
              )}
              {userProfile.spouseAge && userProfile.spouseLiraBalance && userProfile.spouseLiraBalance > 0 && (
                <div className="input-group">
                  <small>Spouse Strategy</small>
                  <select
                    value={userProfile.spouseLiraConversionStrategy || 'Calculate Optimal Conversion Age'}
                    onChange={(e) => handleInputChange('spouseLiraConversionStrategy', e.target.value)}
                  >
                    {LIRA_CONVERSION_STRATEGIES.map(strategy => (
                      <option key={strategy} value={strategy}>{strategy}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <small style={{ color: '#666', fontSize: '0.85rem', display: 'block', marginTop: '0.5rem' }}>
              When to convert LIRA to LIF (separate from retirement age). Choose earlier for income during "grey years"
              (part-time work, consulting), or later for tax optimization. Maximum age 71 (CRA rule).
            </small>
          </div>
        )}

        <div className="form-group">
          <label>TFSA Balance ($)</label>
          <div className="side-by-side-inputs">
            <div className="input-group">
              {userProfile.spouseAge && <small>My TFSA</small>}
              <FormattedInput
                defaultText="Default: $50,000"
                value={userProfile.tfsaBalance}
                onChange={(value) => handleInputChange('tfsaBalance', value)}
                formatOptions={{ type: 'currency', decimalPlaces: 0 }}
              />
            </div>
            {userProfile.spouseAge && (
              <div className="input-group">
                <small>Spouse TFSA</small>
                <FormattedInput
                  defaultText="Default: $45,000"
                  value={userProfile.spouseTfsaBalance || 0}
                  onChange={(value) => handleInputChange('spouseTfsaBalance', value)}
                  formatOptions={{ type: 'currency', decimalPlaces: 0 }}
                />
              </div>
            )}
          </div>
        </div>

        <div className="form-group">
          <label>Other Investment Accounts ($)</label>
          <div className="side-by-side-inputs">
            <div className="input-group">
              {userProfile.spouseAge && <small>My Other Investments</small>}
              <FormattedInput
                defaultText="Default: $100,000"
                value={userProfile.nonRegisteredBalance}
                onChange={(value) => handleInputChange('nonRegisteredBalance', value)}
                formatOptions={{ type: 'currency', decimalPlaces: 0 }}
              />
            </div>
            {userProfile.spouseAge && (
              <div className="input-group">
                <small>Spouse Other Investments</small>
                <FormattedInput
                  defaultText="Default: $80,000"
                  value={userProfile.spouseNonRegisteredBalance || 0}
                  onChange={(value) => handleInputChange('spouseNonRegisteredBalance', value)}
                  formatOptions={{ type: 'currency', decimalPlaces: 0 }}
                />
              </div>
            )}
          </div>
        </div>

        <div className="form-group">
          <label>Corporate Account ($)</label>
          <div className="side-by-side-inputs">
            <div className="input-group">
              {userProfile.spouseAge && <small>My Corporate</small>}
              <FormattedInput
                defaultText="Default: $0"
                value={userProfile.corporateBalance || 0}
                onChange={(value) => handleInputChange('corporateBalance', value)}
                formatOptions={{ type: 'currency', decimalPlaces: 0 }}
              />
            </div>
            {userProfile.spouseAge && (
              <div className="input-group">
                <small>Spouse Corporate</small>
                <FormattedInput
                  defaultText="Default: $0"
                  value={userProfile.spouseCorporateBalance || 0}
                  onChange={(value) => handleInputChange('spouseCorporateBalance', value)}
                  formatOptions={{ type: 'currency', decimalPlaces: 0 }}
                />
              </div>
            )}
          </div>
        </div>

        <div className="form-group">
          <label>TFSA Contribution Room ($)</label>
          <div className="side-by-side-inputs">
            <div className="input-group">
              {userProfile.spouseAge && <small>My Room</small>}
              <FormattedInput
                defaultText="Default: $88,000"
                value={userProfile.tfsaContributionRoom}
                onChange={(value) => handleInputChange('tfsaContributionRoom', value)}
                formatOptions={{ type: 'currency', decimalPlaces: 0 }}
              />
            </div>
            {userProfile.spouseAge && (
              <div className="input-group">
                <small>Spouse Room</small>
                <FormattedInput
                  defaultText="Default: $88,000"
                  value={userProfile.spouseTfsaContributionRoom || 0}
                  onChange={(value) => handleInputChange('spouseTfsaContributionRoom', value)}
                  formatOptions={{ type: 'currency', decimalPlaces: 0 }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialBalancesSection; 